import { useContext } from "react";
import { AppContext } from "../App";
import {
  MdClose,
  MdHome,
  MdInfoOutline,
  MdOutlinePhoneInTalk,
  MdOutlineDashboard,
} from "react-icons/md";
import logo from "../assets/logo.png";

export default function SideBar() {
  const appContext = useContext(AppContext);

  function closeSideBar() {
    appContext.closeSideBar();
  }

  function goTo(item) {
    appContext.navTo({ item });
    closeSideBar();
  }

  const items = [
    { key: "home", label: "Home", icon: <MdHome size={20} /> },
    { key: "about_us", label: "About Us", icon: <MdInfoOutline size={20} /> },
    { key: "services", label: "Services", icon: <MdOutlineDashboard size={20} /> },
    { key: "contact_us", label: "Contact", icon: <MdOutlinePhoneInTalk size={20} /> },
  ];

  const activeNavItem = appContext.navItem || "home";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@300;400;500;600;700;800&display=swap');

        :root {
          --sunflower-amber: #C9A227;
          --sunflower-light: #E8C547;
          --text-navy: #0A0F1C;
          --text-slate: #64748B;
          --sidebar-bg: #050810;
          --sidebar-card: #0F1626;
          --border-subtle: rgba(255, 255, 255, 0.08);
          --font-corporate: 'Libre Franklin', sans-serif;
        }

        /* Subtle background glow */
        .side-modern-body::before {
          content: '';
          position: absolute;
          top: -200px;
          right: -200px;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(201, 162, 39, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .side-modern-body {
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 32px;
          overflow-y: auto;
          z-index: 1;
        }

        /* Hide scrollbar */
        .mNoScrollBar::-webkit-scrollbar { display: none; }
        .mNoScrollBar { -ms-overflow-style: none; scrollbar-width: none; }

        /* --- HEADER / CLOSE BUTTON --- */
        .side-modern-head {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 24px;
        }

        .side-modern-close {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--border-subtle);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .side-modern-close:hover {
          background: var(--sunflower-amber);
          border-color: var(--sunflower-amber);
          color: var(--text-navy);
          transform: rotate(90deg) scale(1.05);
          box-shadow: 0 10px 25px -5px rgba(201, 162, 39, 0.4);
        }

        /* --- LOGO SECTION --- */
        .side-logo-section {
          text-align: center;
          margin-bottom: 48px;
          padding-bottom: 32px;
          border-bottom: 1px solid var(--border-subtle);
        }

        .side-logo-section img {
          height: 56px;
          width: auto;
          opacity: 0.9;
          margin-bottom: 16px;
          transition: opacity 0.3s ease;
        }

        .side-logo-section img:hover {
          opacity: 1;
        }

        .side-logo-tagline {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--sunflower-amber);
          margin: 0;
        }

        /* --- NAVIGATION SECTION --- */
        .side-modern-section {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .side-modern-nav {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .side-modern-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 18px 20px;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 12px;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          text-align: left;
          font-family: var(--font-corporate);
          position: relative;
          overflow: hidden;
        }

        /* Sliding background effect on hover */
        .side-modern-item::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(201, 162, 39, 0.1) 0%, transparent 100%);
          transform: translateX(-100%);
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 0;
        }

        .side-modern-item:hover::before {
          transform: translateX(0);
        }

        .side-modern-item:hover {
          color: #fff;
          border-color: var(--border-subtle);
          transform: translateX(8px);
        }

        /* Active state */
        .side-modern-item[data-active="true"] {
          background: rgba(201, 162, 39, 0.1);
          border-color: var(--sunflower-amber);
          color: #fff;
        }

        .side-modern-item[data-active="true"]::before {
          transform: translateX(0);
          background: linear-gradient(90deg, rgba(201, 162, 39, 0.15) 0%, transparent 100%);
        }

        /* Left accent bar for active state */
        .side-modern-item[data-active="true"]::after {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 60%;
          background: var(--sunflower-amber);
          border-radius: 0 4px 4px 0;
        }

        .side-modern-item-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
        }

        .side-modern-item:hover .side-modern-item-icon {
          background: var(--sunflower-amber);
          color: var(--text-navy);
          transform: scale(1.05);
        }

        .side-modern-item[data-active="true"] .side-modern-item-icon {
          background: var(--sunflower-amber);
          color: var(--text-navy);
        }

        .side-modern-item-label {
          font-size: 0.95rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          position: relative;
          z-index: 1;
          color: black;
        }

        /* --- FOOTER / CONTACT INFO --- */
        .side-footer {
          margin-top: auto;
          padding-top: 32px;
          border-top: 1px solid var(--border-subtle);
        }

        .side-footer-label {
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--sunflower-amber);
          margin-bottom: 16px;
        }

        .side-footer-contact {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .side-footer-item {
          display: flex;
          align-items: center;
          gap: 12px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.85rem;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .side-footer-item:hover {
          color: var(--sunflower-amber);
        }

        .side-footer-item svg {
          flex-shrink: 0;
          color: var(--sunflower-amber);
        }

        /* --- RESPONSIVE --- */
        @media (max-width: 480px) {
          .SideBar { max-width: 100%; }
          .side-modern-body { padding: 24px; }
          .side-modern-item { padding: 16px; }
          .side-modern-item-label { font-size: 0.9rem; }
        }
      `}</style>

      <aside className="SideBar" data-open={appContext.isSideBarOpen} aria-label="Sidebar navigation">
        <div className="side-modern-body mNoScrollBar">
          {/* Header with close button */}
          <div className="side-modern-head">
            <button type="button" className="side-modern-close" onClick={appContext.closeSideBar} aria-label="Close menu">
              <MdClose size={22} />
            </button>
          </div>

          {/* Logo section */}
          <div className="side-logo-section">
            <img src={logo} alt="Divine Love SSP Logo" />
            <p className="side-logo-tagline">Agro-Industrial SPV</p>
          </div>

          {/* Navigation */}
          <div className="side-modern-section">
            <nav className="side-modern-nav" aria-label="Main">
              {items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="side-modern-item"
                  onClick={() => goTo(item.key)}
                  data-active={activeNavItem === item.key}
                >
                  <span className="side-modern-item-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="side-modern-item-label">{item.label}</span>
                </button>
              ))}
            </nav>

            
          </div>
        </div>
      </aside>
    </>
  );
}